import React, { useState } from "react"
import { Button, Typography, Space, Flex } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import FullySubstitutable from "./FullySubstitutable"
import AssignedExperienceNoActiveBooking from "./AssignedExperienceNoActiveBooking"
import PartiallySubstitutable from "./PartiallySubstitutable"
import { Modal } from "ui/Modal"
import { DEACTIVATE_DELETE_MODES, RESOURCE_TYPES } from "../constants"
import { spacings } from "themes/variables"
import styles from "./index.module.css"

const { Title, Text } = Typography

const DeactivateDeleteModal = ({
  visible,
  onCancel,
  onOk,
  title,
  subtitle,
  warning,
  list = [],
  okText,
  cancelText,
  loading = false,
  upcomingBookings = [],
  assignedExperiences = [],
  mode,
  expMessage,
  bookingMessage,
  resourceType = RESOURCE_TYPES.EQUIPMENT,
  translationPrefix = "equipment"
}) => {
  const { t } = useTranslation()
  const [replacementSelections, setReplacementSelections] = useState({})
  const [allSelected, setAllSelected] = useState(false)
  const [isPartiallyValid, setIsPartiallyValid] = useState(false)
  const [partialReplacements, setPartialReplacements] = useState({})

  const handleSelectionsChange = (selections, isAllSelected) => {
    setReplacementSelections(selections)
    setAllSelected(isAllSelected)
  }

  const handleValidityChange = (isValid) => {
    setIsPartiallyValid(isValid)
  }

  const handlePartialSelectionsChange = (selections) => {
    setPartialReplacements(selections)
  }

  const handleOk = () => {
    const data = {}

    if (mode === DEACTIVATE_DELETE_MODES.FULLY_SUBSTITUTABLE && Object.keys(replacementSelections).length > 0) {
      const substitutions = {}
      Object.entries(replacementSelections).forEach(([index, substituteId]) => {
        const booking = upcomingBookings[Number.parseInt(index)]
        if (booking?.booking_id) {
          substitutions[booking.booking_id] = substituteId
        }
      })
      data.substitutions = substitutions
    } else if (mode === DEACTIVATE_DELETE_MODES.PARTIALLY_SUBSTITUTABLE && Object.keys(partialReplacements).length > 0) {
      data.substitutions = partialReplacements
    }

    onOk(data)
  }

  const isOkDisabled =
    (mode === DEACTIVATE_DELETE_MODES.FULLY_SUBSTITUTABLE &&
      upcomingBookings.length > 0 &&
      !allSelected) ||
    (mode === DEACTIVATE_DELETE_MODES.PARTIALLY_SUBSTITUTABLE &&
      upcomingBookings.length > 0 &&
      !isPartiallyValid)

  return (
    <Modal open={visible} onCancel={onCancel} centered width={1050} className={styles.deleteDeactivateModal} maskClosable={false}>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Title level={3}>{title}</Title>
        {subtitle && <Text>{subtitle}</Text>}

        {mode === DEACTIVATE_DELETE_MODES.NO_EXPERIENCES && null}

        {mode === DEACTIVATE_DELETE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING && (
          <AssignedExperienceNoActiveBooking
            list={list}
            warning={warning}
            assignedExperiences={assignedExperiences}
            translationPrefix={translationPrefix}
          />
        )}

        {mode === DEACTIVATE_DELETE_MODES.FULLY_SUBSTITUTABLE && (
          <FullySubstitutable
            upcomingBookings={upcomingBookings}
            assignedExperiences={assignedExperiences}
            warning={warning}
            onSelectionsChange={handleSelectionsChange}
            resourceType={resourceType}
            translationPrefix={translationPrefix}
          />
        )}

        {mode === DEACTIVATE_DELETE_MODES.PARTIALLY_SUBSTITUTABLE && (
          <PartiallySubstitutable
            upcomingBookings={upcomingBookings}
            assignedExperiences={assignedExperiences}
            warning={warning}
            onValidityChange={handleValidityChange}
            onSelectionsChange={handlePartialSelectionsChange}
            expMessage={expMessage}
            bookingMessage={bookingMessage}
            resourceType={resourceType}
            translationPrefix={translationPrefix}
          />
        )}

        <Flex gap={spacings.S}>
          <Button variant="outlined" color="primary" onClick={onCancel}>{cancelText || t("global.cancel")}</Button>
          <Button type="primary" danger onClick={handleOk} loading={loading} disabled={isOkDisabled}>
            {okText || t("global.ok")}
          </Button>
        </Flex>
      </Space>
    </Modal>
  )
}

DeactivateDeleteModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  warning: PropTypes.string,
  list: PropTypes.array,
  okText: PropTypes.string,
  cancelText: PropTypes.string,
  loading: PropTypes.bool,
  upcomingBookings: PropTypes.array,
  assignedExperiences: PropTypes.array,
  mode: PropTypes.oneOf(Object.values(DEACTIVATE_DELETE_MODES)).isRequired,
  expMessage: PropTypes.string,
  bookingMessage: PropTypes.string,
  resourceType: PropTypes.oneOf(Object.values(RESOURCE_TYPES)),
  translationPrefix: PropTypes.string
}

export default DeactivateDeleteModal
